import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { requestSuggestion } from "../../abstract/api/rest";
import * as nodeUtils from "../../abstract/controller/utils/nodeUtils";
import useRequestContainer from "../../abstract/hook/useRequestContainer";
import { getEndpointAI } from "../../abstract/redux/slice/endpointSlice";
import { handleNodeUpdateComment } from "../../abstract/redux/slice/structureSlice";
import IconInfoCircle from "../../themes/icons/IconInfoCircle";
import TooltipComment from "../tooltip/TooltipComment";
import TooltipIconComment from "../tooltip/TooltipIconComment";
import * as vendorTooltip from "../ui/tooltips/DefaultTooltip";
import styles from "./ElementComment.module.scss";

const _regex = /\r?\n|\r|\n/g;

const _placeholderSuggestion = "Loading suggestion...";
const _placeholderDefault =
  "To add a new line press 'shift + enter'.\nTo accept changes press 'enter'.";

function ElementComment({ node }) {
  const _dispatch = useDispatch();

  const _endpointAI = useSelector(getEndpointAI);

  const _id = nodeUtils.getID(node) ?? "";
  const _uuid = nodeUtils.getUUID(node) ?? "";
  const _comment = nodeUtils.getComment(node) ?? "";

  const _container = useRequestContainer({ id: _id, nodeParent: node, type: "comment" });

  const [_placeholder, _setPlaceholder] = useState(_placeholderDefault);

  const [_optionComment, _setOptionComment] = useState(_comment);
  const [_optionRows, _setOptionRows] = useState(2);
  const [_isLoadingSuggestion, _setIsLoadingSuggestion] = useState(false);

  const _commentRef = useRef(null);
  const _suggestionTimeoutRef = useRef(null);
  const _abortRef = useRef(null);

  // const resizeTextArea = (comment) => {
  //   const _lines = comment.split(_regex);
  //   const _size = _lines.map(e => 1+ (e / 30)).reduce((acc, val) => acc + val, 0);

  //   return _size;
  // }

  useEffect(() => {
    const _lines = _comment.split(_regex);
    const _size = _lines.map((e) => Math.ceil(e.length / 35)).reduce((acc, val) => acc + val, 0);

    _setOptionRows(_size);
  }, [_comment]);

  const clearSuggestionTimeout = useCallback(() => {
    if (_suggestionTimeoutRef.current) {
      clearTimeout(_suggestionTimeoutRef.current);
      _suggestionTimeoutRef.current = null;
    }

    _setIsLoadingSuggestion(false);
  }, []);

  const actionChangeCommentAccept = useCallback(() => {
    clearSuggestionTimeout();

    _dispatch(handleNodeUpdateComment(_id, _optionComment));
  }, [clearSuggestionTimeout, _dispatch, _id, _optionComment]);

  const handleInputCommentOnChange = (e) => {
    const _value = e.target.value;

    _setOptionComment(_value);
    _setPlaceholder(_placeholderDefault);

    if (_value && _value.trim() !== "") {
      if (_commentRef.current.selectionEnd >= _commentRef.current.textLength) {
        suggestCommentSuffix(_value);
      } else {
        clearSuggestionTimeout();
      }
    }
  };

  const handleInputCommentFocusOut = () => {
    if (_isLoadingSuggestion) {
      return;
    }

    clearSuggestionTimeout();

    actionChangeCommentAccept();
  };

  const handleInputCommentFocusIn = () => {
    if (_isLoadingSuggestion) {
      return;
    }

    if (!_optionComment || _optionComment.trim() === "") {
      suggestComment();
    } else {
      _commentRef.current.select();
    }
  };

  const suggestComment = useCallback(async () => {
    if (!_endpointAI) {
      return;
    }
    
    _setIsLoadingSuggestion(true);
    _setPlaceholder(_placeholderSuggestion);

    _abortRef.current = new AbortController();

    try {
      const _suggestion = await requestSuggestion({
        controller: _abortRef.current,
        ..._container.request,
      });

      if (_commentRef.current) {
        if (_suggestion.status !== "error" && !_commentRef.current.value) {
          _setOptionComment(_suggestion.message);

          _commentRef.current.value = _suggestion.message;
          _commentRef.current.select();
        } else {
          _setPlaceholder("No available suggestions");
        }
      }
    } catch (error) {}

    clearSuggestionTimeout();
  }, [_container.request, _endpointAI, clearSuggestionTimeout]);

  const suggestCommentSuffix = useCallback(
    async (prefix) => {
      if (!_endpointAI) {
        return;
      }

      if (_suggestionTimeoutRef.current) {
        return;
      }

      _abortRef.current = new AbortController();

      _suggestionTimeoutRef.current = setTimeout(async () => {
        _setIsLoadingSuggestion(true);

        try {
          const _suggestion = await requestSuggestion({
            controller: _abortRef.current,
            prefix,
            ..._container.request,
          });

          if (_suggestion.status !== "error") {
            _setOptionComment(_suggestion.message);

            if (_commentRef.current) {
              _commentRef.current.value = _suggestion.message;
              _commentRef.current.setSelectionRange(prefix.length, _suggestion.message.length);
            }
          } else {
            _setPlaceholder("No available suggestions");
          }
        } catch (error) {}

        clearSuggestionTimeout();
      }, 2000);
    },
    [_container.request, _endpointAI, clearSuggestionTimeout]
  );

  useEffect(() => {
    _setOptionComment(_comment);
  }, [_comment]);

  useEffect(() => {
    const _target = _commentRef.current;

    const callback = (e) => {
      if (e.code === "Escape") {
        clearSuggestionTimeout();
      } else if (e.code === "Enter" && !e.shiftKey) {
        e.preventDefault();

        if (!_isLoadingSuggestion) {
          actionChangeCommentAccept();
        }
      } else if (e.code === "ArrowRight") {
        if (_target.selectionEnd + 1 >= _target.textLength) {
          suggestCommentSuffix(_target.defaultValue);
        }
      } else {
        clearSuggestionTimeout();
      }
    };

    _target?.addEventListener("keydown", callback);

    return () => _target?.removeEventListener("keydown", callback);
  }, [
    _isLoadingSuggestion,
    actionChangeCommentAccept,
    clearSuggestionTimeout,
    suggestCommentSuffix,
  ]);

  useEffect(() => {
    return () => {
      if (_suggestionTimeoutRef.current) {
        clearTimeout(_suggestionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={styles["meta__option"]}>
      <div className={styles["meta__option-header"]}>
        <div className={styles["meta__option-header-name"]}>Comment</div>
        <div className={styles["meta__option-header-icon"]}>
          <IconInfoCircle
            data-tooltip-id={`meta-tooltip-comment-${_uuid}`}
            data-tooltip-delay-show={vendorTooltip.getTimeFast()}
          />
          <TooltipIconComment handle={`meta-tooltip-comment-${_uuid}`} />
        </div>
      </div>
      <textarea
        ref={_commentRef}
        value={_optionComment}
        rows={_optionRows < 2 ? 2 : _optionRows}
        placeholder={_placeholder}
        onChange={handleInputCommentOnChange}
        onFocus={handleInputCommentFocusIn}
        onBlur={handleInputCommentFocusOut}
        className={`${styles["meta__option-input-area"]} ${_isLoadingSuggestion ? styles.shimmer : ""}`}
        data-title={_optionComment}
        data-tooltip-id={`meta-comment-${_uuid}`}
        data-tooltip-delay-show={vendorTooltip.getTimeFast()}
      />
      <TooltipComment node={node} handle={`meta-comment-${_uuid}`} />
    </div>
  );
}

export default ElementComment;
